/**
 * Beybling — cart drawer: Checkout + Apple Pay only.
 *
 * Shopify's <shopify-accelerated-checkout-cart> uses a closed shadow grid that
 * reserves 48px per configured wallet. Hiding slotted Shop Pay / PayPal / Google
 * Pay in CSS does not collapse those rows — we clip the wrapper and translate
 * the host to the Apple Pay row.
 *
 * Why Apple Pay is "slow":
 *  - Shopify's wallet library is lazy-registered; its FIRST render is slow by
 *    design (docs: "render more slowly on their first request"). In a drawer,
 *    every session's first open is that first request.
 *  - The drawer is display:none until opened, so the component can't lay out
 *    until then — and a zero-height/clipped host defers the render further.
 *
 * Strategy for "instant feel":
 *  - On Apple-capable devices, reserve ONE button-height slot up front (CSS) so
 *    Shopify renders into a visible box immediately and the customer sees the
 *    native skeleton instead of empty space or a late pop-in.
 *  - Warm the library on idle so registration is done before the drawer opens.
 *  - One clip pass when Apple Pay paints; no height thrashing.
 */

const CART_DRAWER_ID = 'beybling-cart-drawer';
const CART_DRAWER_REFLOW_EVENT = 'beybling:cart-drawer-reflow';
const APPLE_DEVICE_CLASS = 'beybling-apple-pay-device';
const BUTTON_HEIGHT = 48;
const APPLE_RE = /apple/i;
const STABLE_ATTR = 'data-beybling-wallet-stable';
const DISMISS_ATTR = 'data-beybling-wallet-dismissed';
const DISMISS_AFTER_MS = 6000;
const RETRY_MS = 1200;
const MARKER_ATTRS = ['slot', 'class', 'id', 'aria-label', 'data-testid', 'name', 'role'];
const IGNORED_TAGS = new Set(['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE']);

/** @type {WeakMap<HTMLElement, number>} */
const lastOffsets = new WeakMap();
/** @type {WeakMap<HTMLElement, ReturnType<typeof setTimeout>>} */
const dismissTimers = new WeakMap();

/**
 * @param {HTMLElement} element
 * @param {string} property
 * @param {string} value
 */
function setImportant(element, property, value) {
  if (element.style.getPropertyValue(property) === value) return;
  element.style.setProperty(property, value, 'important');
}

function deviceCanUseApplePay() {
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return true;

  try {
    return typeof ApplePaySession !== 'undefined' && ApplePaySession.canMakePayments();
  } catch {
    return false;
  }
}

function markApplePayDevice() {
  if (deviceCanUseApplePay()) {
    document.documentElement.classList.add(APPLE_DEVICE_CLASS);
  }
}

/**
 * Nudge Shopify's wallet library to download + register before the drawer is
 * opened, so the in-drawer render is a "subsequent" (fast) one, not the slow
 * first request. Also primes the Apple Pay entitlement check.
 */
function warmUpWallets() {
  try {
    if (typeof ApplePaySession !== 'undefined' && ApplePaySession.canMakePayments) {
      ApplePaySession.canMakePayments();
    }
  } catch {
    /* entitlement check can throw in cross-origin frames — ignore */
  }

  try {
    customElements.whenDefined('shopify-accelerated-checkout-cart').then(() => scheduleFilter());
  } catch {
    /* whenDefined unsupported — ignore */
  }
}

/**
 * @param {Element} element
 * @returns {boolean}
 */
function elementMentionsApple(element) {
  if (APPLE_RE.test(element.tagName)) return true;

  return MARKER_ATTRS.some((name) => {
    const value = element.getAttribute(name);
    return typeof value === 'string' && APPLE_RE.test(value);
  });
}

/**
 * @param {Element} element
 * @returns {boolean}
 */
function isApplePayBranch(element) {
  if (elementMentionsApple(element)) return true;

  for (const node of element.querySelectorAll('*')) {
    if (elementMentionsApple(node)) return true;
  }

  return false;
}

/**
 * @param {HTMLElement} host
 * @returns {HTMLElement | null}
 */
function findAppleBranch(host) {
  for (const child of host.children) {
    if (!(child instanceof HTMLElement) || IGNORED_TAGS.has(child.tagName)) continue;
    if (isApplePayBranch(child)) return child;
  }
  return null;
}

/**
 * @param {HTMLElement} apple
 * @returns {boolean}
 */
function appleHasPainted(apple) {
  return apple.getBoundingClientRect().height >= 20;
}

/**
 * @param {HTMLElement} element
 */
function collapseElement(element) {
  if (element.dataset.beyblingWallet === 'collapsed') return;
  element.dataset.beyblingWallet = 'collapsed';

  setImportant(element, 'display', 'none');
  setImportant(element, 'height', '0');
  setImportant(element, 'min-height', '0');
  setImportant(element, 'max-height', '0');
  setImportant(element, 'margin', '0');
  setImportant(element, 'padding', '0');
  setImportant(element, 'overflow', 'hidden');
}

/**
 * @param {HTMLElement} element
 */
function revealElement(element) {
  if (element.dataset.beyblingWallet !== 'revealed') {
    element.dataset.beyblingWallet = 'revealed';
    element.hidden = false;
    setImportant(element, 'display', 'block');
    element.style.removeProperty('height');
    element.style.removeProperty('max-height');
    setImportant(element, 'min-height', '0');
    setImportant(element, 'margin', '0');
    setImportant(element, 'padding', '0');
    setImportant(element, 'overflow', 'visible');
  }
}

/**
 * @param {HTMLElement} host
 * @returns {HTMLElement | null}
 */
function getWalletOuter(host) {
  const outer = host.closest('.beybling-cart-apple-pay, .additional-checkout-buttons');
  return outer instanceof HTMLElement ? outer : null;
}

/**
 * @param {HTMLElement} host
 */
function clearDismissTimer(host) {
  const timer = dismissTimers.get(host);
  if (timer) {
    clearTimeout(timer);
    dismissTimers.delete(host);
  }
}

/**
 * Hide the wallet block entirely (non-Apple devices, or Apple Pay never paints).
 *
 * @param {HTMLElement} host
 */
function hideWalletBlock(host) {
  const outer = getWalletOuter(host);
  const wrapper = host.closest('.dynamic-checkout__content');

  outer?.removeAttribute(STABLE_ATTR);

  if (wrapper instanceof HTMLElement) {
    setImportant(wrapper, 'height', '0');
    setImportant(wrapper, 'overflow', 'hidden');
  }

  setImportant(host, 'transform', 'translateY(0)');
  host.style.removeProperty('margin-top');
  lastOffsets.delete(host);
}

/**
 * Reserve a single button-height slot so Shopify renders into a visible box
 * immediately (showing its skeleton) instead of deferring on a zero-size host.
 *
 * @param {HTMLElement} host
 * @param {HTMLElement} outer
 */
function reserveSkeletonSlot(host, outer) {
  const wrapper = host.closest('.dynamic-checkout__content');

  outer.removeAttribute(DISMISS_ATTR);

  if (wrapper instanceof HTMLElement) {
    setImportant(wrapper, 'height', `${BUTTON_HEIGHT}px`);
    setImportant(wrapper, 'overflow', 'hidden');
    setImportant(wrapper, 'margin', '0');
    setImportant(wrapper, 'padding', '0');
  }

  setImportant(host, 'display', 'inline-block');
  setImportant(host, 'width', '100%');
  host.style.removeProperty('margin-top');
  setImportant(host, 'transform', 'translateY(0)');
}

/**
 * @param {HTMLElement} host
 * @param {HTMLElement} outer
 */
function scheduleDismissIfNeeded(host, outer) {
  if (dismissTimers.has(host)) return;

  dismissTimers.set(
    host,
    setTimeout(() => {
      dismissTimers.delete(host);

      const apple = findAppleBranch(host);
      if (apple && appleHasPainted(apple)) {
        showApplePayRow(host, apple);
        return;
      }

      outer.setAttribute(DISMISS_ATTR, 'true');
      hideWalletBlock(host);
    }, DISMISS_AFTER_MS),
  );
}

/**
 * @param {HTMLElement} host
 * @param {HTMLElement} apple
 */
function showApplePayRow(host, apple) {
  const wrapper = host.closest('.dynamic-checkout__content');
  const outer = getWalletOuter(host);
  if (!(wrapper instanceof HTMLElement) || !outer) return;

  clearDismissTimer(host);
  revealElement(apple);

  const offset = Math.max(
    0,
    Math.round(apple.getBoundingClientRect().top - host.getBoundingClientRect().top),
  );
  const previousOffset = lastOffsets.get(host);

  setImportant(wrapper, 'overflow', 'hidden');
  setImportant(wrapper, 'height', `${BUTTON_HEIGHT}px`);
  setImportant(wrapper, 'margin', '0');
  setImportant(wrapper, 'padding', '0');
  setImportant(host, 'display', 'inline-block');
  setImportant(host, 'width', '100%');
  host.style.removeProperty('margin-top');
  setImportant(host, 'transform', `translateY(${-offset}px)`);

  outer.removeAttribute(DISMISS_ATTR);

  if (previousOffset !== offset || !outer.hasAttribute(STABLE_ATTR)) {
    outer.setAttribute(STABLE_ATTR, 'true');
    lastOffsets.set(host, offset);
  }
}

/**
 * @param {HTMLElement} host
 */
function layoutWalletHost(host) {
  // Wallet by platform: Apple Pay on iOS, Google Pay on Android, PayPal everywhere else.
  const ua = navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  document.documentElement.setAttribute('data-bb-wallet', isIOS ? 'apple' : isAndroid ? 'google' : 'paypal');
  if (!isIOS) {
    const want = isAndroid ? /google/i : /paypal/i;
    Array.from(host.children).forEach((child) => {
      if (!(child instanceof HTMLElement) || IGNORED_TAGS.has(child.tagName)) return;
      if (want.test(child.tagName)) revealElement(child);
      else collapseElement(child);
    });
    return;
  }
  const outer = getWalletOuter(host);
  if (!outer) return;

  const apple = findAppleBranch(host);

  Array.from(host.children).forEach((child) => {
    if (!(child instanceof HTMLElement) || IGNORED_TAGS.has(child.tagName)) return;

    if (child === apple) {
      revealElement(child);
    } else {
      collapseElement(child);
    }
  });

  if (apple && appleHasPainted(apple)) {
    showApplePayRow(host, apple);
    return;
  }

  if (deviceCanUseApplePay()) {
    reserveSkeletonSlot(host, outer);
    scheduleDismissIfNeeded(host, outer);
    return;
  }

  outer.setAttribute(DISMISS_ATTR, 'true');
  hideWalletBlock(host);
}

function filterCartDrawerWallets() {
  const cartDrawer = document.getElementById(CART_DRAWER_ID);
  if (!cartDrawer) return;

  cartDrawer.querySelectorAll('shopify-accelerated-checkout-cart').forEach((host) => {
    if (host instanceof HTMLElement) layoutWalletHost(host);
  });
}

let filterScheduled = false;
/** @type {ReturnType<typeof setTimeout> | null} */
let retryTimer = null;
/** @type {MutationObserver | null} */
let walletObserver = null;

function scheduleFilter() {
  if (filterScheduled) return;
  filterScheduled = true;

  requestAnimationFrame(() => {
    filterScheduled = false;
    filterCartDrawerWallets();
  });
}

function scheduleRetry() {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    filterCartDrawerWallets();
  }, RETRY_MS);
}

function runFilterPass() {
  scheduleFilter();
  scheduleRetry();
}

/**
 * @param {MutationRecord[]} mutations
 * @returns {boolean}
 */
function mutationsTouchWalletArea(mutations) {
  return mutations.some((mutation) => {
    if (mutation.type === 'attributes') {
      const target = mutation.target;
      return target instanceof Element && target.matches('shopify-accelerated-checkout-cart');
    }

    return Array.from(mutation.addedNodes).some(
      (node) =>
        node instanceof Element &&
        (node.matches('shopify-accelerated-checkout-cart') ||
          node.querySelector('shopify-accelerated-checkout-cart')),
    );
  });
}

/**
 * @param {HTMLElement} cartDrawer
 */
function bindWalletObserver(cartDrawer) {
  walletObserver?.disconnect();

  const footer = cartDrawer.querySelector('.cart-drawer__footer');
  if (!(footer instanceof HTMLElement)) {
    walletObserver = null;
    return;
  }

  walletObserver = new MutationObserver((mutations) => {
    if (mutationsTouchWalletArea(mutations)) scheduleFilter();
  });
  walletObserver.observe(footer, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['wallet-configs'],
  });
}

function scheduleWarmUp() {
  const run = () => warmUpWallets();
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 1000);
  }
}

function init() {
  markApplePayDevice();

  const cartDrawer = document.getElementById(CART_DRAWER_ID);
  if (!(cartDrawer instanceof HTMLElement)) return;

  bindWalletObserver(cartDrawer);
  runFilterPass();
  scheduleWarmUp();

  cartDrawer.addEventListener('dialog:open', () => {
    markApplePayDevice();
    bindWalletObserver(cartDrawer);
    runFilterPass();
  });

  document.addEventListener('cart:update', runFilterPass);
  document.addEventListener(CART_DRAWER_REFLOW_EVENT, () => {
    bindWalletObserver(cartDrawer);
    runFilterPass();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
